import React, { useState, useRef } from 'react';
import { Upload, X, Download, Eye, FileText, File, Image, Paperclip } from 'lucide-react';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  uploadedAt: Date;
}

interface FileUploadProps {
  label: string;
  description?: string;
  value: UploadedFile[];
  onSave: (files: UploadedFile[]) => Promise<void>;
  disabled?: boolean;
  multiple?: boolean;
  acceptedTypes?: string[];
  maxSize?: number; // en MB
  required?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  description,
  value = [],
  onSave,
  disabled = false,
  multiple = true,
  acceptedTypes = ['.pdf', '.doc', '.docx', '.jpg', '.png'],
  maxSize = 5,
  required = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    // Vérifier la taille
    if (file.size > maxSize * 1024 * 1024) {
      return `Le fichier "${file.name}" dépasse la taille maximale de ${maxSize}MB`;
    }

    // Vérifier le type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedTypes.includes(fileExtension)) {
      return `Le type de fichier "${fileExtension}" n'est pas accepté`;
    }

    return null;
  };

  const handleFiles = async (files: FileList) => {
    if (disabled) return;

    setIsUploading(true);
    const newFiles: UploadedFile[] = [];
    const errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const error = validateFile(file);
      
      if (error) {
        errors.push(error);
        continue;
      }

      // Simuler un upload (à remplacer par vraie logique d'upload)
      const uploadedFile: UploadedFile = {
        id: `file_${Date.now()}_${i}`,
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file), // Temporaire pour preview
        uploadedAt: new Date()
      };

      newFiles.push(uploadedFile);
    }

    if (errors.length > 0) {
      alert('Erreurs lors de l\'upload:\n' + errors.join('\n'));
    }

    if (newFiles.length > 0) {
      const updatedFiles = multiple ? [...value, ...newFiles] : newFiles;
      await onSave(updatedFiles);
    }

    setIsUploading(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Vérifier si on quitte vraiment la zone de drop
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleRemoveFile = async (fileId: string) => {
    if (disabled) return;
    const updatedFiles = value.filter(f => f.id !== fileId);
    await onSave(updatedFiles);
  };

  const handleBrowseClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return <File className="h-4 w-4 text-red-500" />;
      case 'doc':
      case 'docx': return <FileText className="h-4 w-4 text-blue-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png': return <Image className="h-4 w-4 text-green-500" />;
      default: return <Paperclip className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      </div>
      
      {description && (
        <p className="text-sm text-gray-500">{description}</p>
      )}

      {/* Zone de drop */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200
          ${isDragging 
            ? 'border-[#197ca8] bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onClick={handleBrowseClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={acceptedTypes.join(',')}
          onChange={handleFileInput}
          className="hidden"
          disabled={disabled}
        />

        <div className="flex flex-col items-center space-y-2">
          <Upload className={`w-8 h-8 ${isDragging ? 'text-[#197ca8]' : 'text-gray-400'}`} />
          <div className="text-sm">
            <span className="font-medium text-[#197ca8]">
              Cliquez pour parcourir
            </span>
            <span className="text-gray-500"> ou glissez-déposez vos fichiers ici</span>
          </div>
          <div className="text-xs text-gray-500">
            Formats acceptés: {acceptedTypes.join(', ')} • Taille max: {maxSize}MB
            {multiple && ' • Plusieurs fichiers autorisés'}
          </div>
        </div>

        {isUploading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#197ca8]"></div>
              <span className="text-sm text-gray-600">Upload en cours...</span>
            </div>
          </div>
        )}
      </div>

      {/* Liste des fichiers uploadés */}
      {value.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Documents joints ({value.length})
          </h4>
          <div className="space-y-2">
            {value.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {getFileIcon(file.name)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)} • {file.uploadedAt.toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {file.url && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(file.url, '_blank');
                        }}
                        className="p-1 text-gray-400 hover:text-[#197ca8] transition-colors"
                        title="Voir le fichier"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const link = document.createElement('a');
                          link.href = file.url!;
                          link.download = file.name;
                          link.click();
                        }}
                        className="p-1 text-gray-400 hover:text-[#197ca8] transition-colors"
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {!disabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(file.id);
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Supprimer"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;